import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { Card } from "@repo/ui/components/ui/card";
import { Progress } from "@repo/ui/components/ui/progress";
import { Separator } from "@repo/ui/components/ui/separator";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { useGroupe } from "@repo/ui/hooks/api/groupe/index";
import { formatDate } from "@repo/ui/lib/utils";
import {
  ArrowLeft,
  Pencil,
  Building2,
  Users,
  CreditCard,
  Calendar,
  MapPin,
  FileText,
  TreeDeciduous,
  Layers,
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  Infinity,
  TrendingUp,
  Package
} from "lucide-react";

interface GroupeDetailProps {
  groupeId: number;
  onBack: () => void;
  onEdit: () => void;
}

function DetailRow({
  icon: Icon,
  label,
  value
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      <span className="text-sm font-semibold text-foreground">{value ?? "-"}</span>
    </div>
  );
}

function UsageCard({
  title,
  icon: Icon,
  current,
  max,
  color = "blue"
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  current: number;
  max: number;
  color?: "blue" | "green" | "purple";
}) {
  const isUnlimited = max === -1;
  const percentage = isUnlimited ? 0 : (current / max) * 100;

  const colorClasses = {
    blue: "text-blue-600 bg-blue-50",
    green: "text-green-600 bg-green-50",
    purple: "text-purple-600 bg-purple-50"
  };

  const progressColorClasses = {
    blue: "[&>div]:bg-blue-500",
    green: "[&>div]:bg-green-500",
    purple: "[&>div]:bg-purple-500"
  };

  return (
    <Card className="p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold">{current}</span>
            {isUnlimited ? (
              <div className="flex items-center gap-1 text-green-600">
                <Infinity className="h-4 w-4" />
                <span className="text-sm font-medium">Illimité</span>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">/ {max}</span>
            )}
          </div>
        </div>
      </div>

      {!isUnlimited && (
        <div className="space-y-2">
          <Progress value={percentage} className={progressColorClasses[color]} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{percentage.toFixed(0)}% utilisé</span>
            <span>{max - current} restants</span>
          </div>
        </div>
      )}

      {isUnlimited && (
        <div className="text-xs text-center text-muted-foreground bg-muted/50 py-2 rounded">
          Aucune limite sur cette ressource
        </div>
      )}
    </Card>
  );
}

export function GroupeDetail({ groupeId, onBack, onEdit }: GroupeDetailProps) {
  const { data: groupeData, isLoading, error } = useGroupe(groupeId);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (error) return <div className="text-red-500 p-4">Erreur : {error.message}</div>;
  if (!groupeData?.data) return <div className="p-4">Groupe non trouvé</div>;

  const groupe = groupeData.data as any;
  const planDetails = groupe.planDetails;

  // Get subscription status badge
  const getSubscriptionBadge = () => {
    const status = planDetails?.status || groupe.subscription_status || "ACTIVE";
    if (status === "ACTIVE") {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Actif</Badge>;
    }
    if (status === "TRIAL") {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><AlertCircle className="h-3 w-3 mr-1" /> Essai</Badge>;
    }
    return <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" /> Inactif</Badge>;
  };

  const getPlanBadge = () => {
    const plan = planDetails?.planType || groupe.plan_type || "BASIC";
    if (plan === "ADVANCED") {
      return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Advanced</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Basic</Badge>;
  };

  const unifiedCurrent = planDetails
    ? planDetails.usage.currentMonthParcours
    : (groupe.current_month_parcours ?? 0);
  const unifiedMax = planDetails
    ? planDetails.limits.maxParcoursPerMonth
    : (groupe.max_parcours_per_month ?? 0);

  return (
    <div className="space-y-6">
      {/* Header avec actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </Button>
        <Button onClick={onEdit} className="gap-2">
          <Pencil className="w-4 h-4" /> Modifier
        </Button>
      </div>

      {/* Header Card avec logo et infos principales */}
      <Card className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Logo */}
          <div className="flex-shrink-0">
            {groupe.img_logo ? (
              <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <ImageIcon className="w-10 h-10 text-primary" />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <Building2 className="w-10 h-10 text-primary" />
              </div>
            )}
          </div>

          {/* Infos principales */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-1">{groupe.name}</h2>
                <p className="text-muted-foreground flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {groupe.name_societe || "Aucune société associée"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {getSubscriptionBadge()}
                {getPlanBadge()}
                <Badge variant="outline">
                  <Users className="h-3 w-3 mr-1" />
                  {groupe.licence} {groupe.licence > 1 ? "licences" : "licence"}
                </Badge>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Type:</span>
                <span className="font-medium">{groupe.groupe_type}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Échéance:</span>
                <span className="font-medium">{formatDate(groupe.date_echeance)}</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Tier:</span>
                <span className="font-medium">
                  {planDetails?.tier?.title || groupe.tier_id || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Usage Cards */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Utilisation ce mois-ci
        </h3>

        {/* Carte unifiée — compteur principal */}
        <UsageCard
          title="Total Parcours"
          icon={Layers}
          current={unifiedCurrent}
          max={unifiedMax}
          color="green"
        />

      </div>

      {/* Plan Details Section (if enriched data available) */}
      {planDetails && (
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <Package className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold">{planDetails.tier.title}</h3>
                {planDetails.tier.subTitle && (
                  <span className="text-sm text-muted-foreground">• {planDetails.tier.subTitle}</span>
                )}
                {planDetails.tier.price && (
                  <Badge variant="outline" className="ml-auto">
                    {planDetails.tier.price.toFixed(2)}€/mois
                  </Badge>
                )}
              </div>


            </div>
          </div>
        </Card>
      )}

      {/* Informations détaillées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section: Informations de la société */}
        <Card className="p-6">
          <Section title="Informations de la société" icon={Building2}>
            <DetailRow icon={FileText} label="SIRET" value={groupe.siret} />
            <DetailRow icon={FileText} label="Certification" value={groupe.num_certification} />
            <DetailRow icon={MapPin} label="Adresse" value={groupe.address_societe} />
            <DetailRow icon={MapPin} label="Code postal" value={groupe.cp} />
            <DetailRow icon={MapPin} label="Ville" value={groupe.ville} />
          </Section>
        </Card>

        {/* Section: Abonnement */}
        <Card className="p-6">
          <Section title="Informations d'abonnement" icon={CreditCard}>
            <DetailRow icon={CreditCard} label="ID d'abonnement" value={groupe.sub_id} />
            <DetailRow icon={CreditCard} label="ID client" value={groupe.customer_id} />
            <DetailRow icon={Calendar} label="Date d'abonnement" value={formatDate(groupe.date_subscribed)} />
            <DetailRow icon={Calendar} label="Date d'échéance" value={formatDate(groupe.date_echeance)} />

          </Section>
        </Card>
      </div>

      {/* Métadonnées */}
      <Card className="p-6">
        <Section title="Métadonnées" icon={FileText}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">ID du groupe</p>
              <p className="text-sm font-mono font-semibold">{groupe.id}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Créé le</p>
              <p className="text-sm font-semibold">{formatDate(groupe.created_at)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Dernière mise à jour</p>
              <p className="text-sm font-semibold">{formatDate(groupe.updated_at)}</p>
            </div>
          </div>
        </Section>
      </Card>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-base font-semibold mb-4 flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5 text-primary" />}
        {title}
      </h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
